import { BUILTIN_SCENES, merge, SCENE_JINDIAN } from './look';
import { TEST_TRACKS, testTimeline } from './testTones';
import { parseTimeline } from './timeline';
import type { Catalog, Look, Playlist, ScenePreset, Timeline, Track } from './types';

export const LOCAL_PLAYLIST_ID = 'local-import';
const EDIT_KEY = (id: string) => `nianfo.timeline.edit.${id}`;

/**
 * 曲库：合并 catalog.json（正式播单）、调试测试音播单、本地导入播单。
 */
export class Library {
  tracks = new Map<string, Track>();
  playlists: Playlist[] = [];
  scenes = new Map<string, ScenePreset>();
  catalogError: string | null = null;
  private timelineCache = new Map<string, Timeline | null>();
  private catalogBase = new URL('./catalog/', location.href);

  constructor() {
    for (const s of BUILTIN_SCENES) this.scenes.set(s.id, s);
  }

  async load(url = './catalog/catalog.json'): Promise<void> {
    const abs = new URL(url, location.href);
    this.catalogBase = new URL('./', abs);
    try {
      const res = await fetch(abs, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cat = (await res.json()) as Catalog;
      this.applyCatalog(cat);
    } catch (e) {
      this.catalogError = `曲目清单读取失败：${(e as Error).message}`;
      console.warn(this.catalogError);
    }
    for (const t of TEST_TRACKS) this.tracks.set(t.id, t);
  }

  private applyCatalog(cat: Catalog): void {
    for (const [id, s] of Object.entries(cat.scenes ?? {})) {
      const base = this.scenes.get(s.base ?? 'jindian')?.look ?? SCENE_JINDIAN;
      const look = merge(base, s);
      look.name = s.name;
      this.scenes.set(id, { id, name: s.name, look, builtin: true });
    }
    for (const t of cat.tracks ?? []) {
      const src = t.src ? new URL(t.src, this.catalogBase).href : null;
      const timeline = typeof t.timeline === 'string' ? new URL(t.timeline, this.catalogBase).href : t.timeline;
      this.tracks.set(t.id, { ...t, src, timeline, kind: 'catalog' });
    }
    for (const p of cat.playlists ?? []) {
      const missing = p.tracks.filter((id) => !this.tracks.has(id));
      if (missing.length) console.warn(`播单 ${p.id} 引用了不存在的曲目：`, missing);
      this.playlists.push({ ...p, kind: 'catalog', tracks: p.tracks.filter((id) => this.tracks.has(id)) });
    }
  }

  playlist(id: string): Playlist | undefined {
    return this.playlists.find((p) => p.id === id);
  }

  /** 第一个有可播放曲目的播单 */
  firstPlayable(): Playlist | undefined {
    return this.playlists.find((p) => p.tracks.some((id) => this.isPlayable(this.tracks.get(id))));
  }

  isPlayable(t: Track | undefined): boolean {
    return !!t && (t.kind === 'test-tone' || !!t.src);
  }

  /** 场景解析：场景(曲目 > 播单 > 默认) → 播单覆盖 → 曲目覆盖 */
  lookFor(track: Track | undefined, playlist: Playlist | undefined): Look {
    const sceneId = track?.scene ?? playlist?.scene ?? 'jindian';
    const base = this.scenes.get(sceneId)?.look ?? SCENE_JINDIAN;
    return merge(merge(base, playlist?.look), track?.look);
  }

  sceneIdFor(track: Track | undefined, playlist: Playlist | undefined): string {
    return track?.scene ?? playlist?.scene ?? 'jindian';
  }

  /* ---------------- 时间轴 ---------------- */

  /** 读取时间轴：本地编辑版 > 曲目自带（内联或外部文件）> 测试音生成 */
  async timelineFor(track: Track): Promise<{ timeline: Timeline | null; edited: boolean }> {
    const edited = this.loadEdited(track.id);
    if (edited) return { timeline: edited, edited: true };
    return { timeline: await this.originalTimeline(track), edited: false };
  }

  async originalTimeline(track: Track): Promise<Timeline | null> {
    if (this.timelineCache.has(track.id)) return structuredClone(this.timelineCache.get(track.id) ?? null);
    let tl: Timeline | null = null;
    try {
      if (track.kind === 'test-tone' && track.testTone) tl = testTimeline(track.testTone, track.id);
      else if (typeof track.timeline === 'string') {
        const res = await fetch(track.timeline, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        tl = parseTimeline(await res.text(), track.id);
      } else if (track.timeline) tl = parseTimeline(JSON.stringify(track.timeline), track.id);
    } catch (e) {
      console.warn(`时间轴读取失败（${track.id}）：`, e);
    }
    this.timelineCache.set(track.id, tl);
    return structuredClone(tl);
  }

  loadEdited(trackId: string): Timeline | null {
    try {
      const raw = localStorage.getItem(EDIT_KEY(trackId));
      return raw ? parseTimeline(raw, trackId) : null;
    } catch {
      return null;
    }
  }

  saveEdited(trackId: string, tl: Timeline): void {
    localStorage.setItem(EDIT_KEY(trackId), JSON.stringify(tl));
  }

  clearEdited(trackId: string): void {
    localStorage.removeItem(EDIT_KEY(trackId));
  }

  /** 本地导入的曲目时间轴放在内存里（文件 URL 只在本次会话有效） */
  setInlineTimeline(trackId: string, tl: Timeline): void {
    const t = this.tracks.get(trackId);
    if (t) t.timeline = tl;
    this.timelineCache.delete(trackId);
  }

  /* ---------------- 本地导入 ---------------- */

  /**
   * 导入本地音频（以及同名的 .json / .lrc 时间轴）。仅在当前浏览器会话中有效。
   * 返回新加入的曲目 id。
   */
  async addLocalFiles(files: File[]): Promise<string[]> {
    const audio = files.filter((f) => f.type.startsWith('audio/') || /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|webm)$/i.test(f.name));
    const timelines = files.filter((f) => /\.(json|lrc)$/i.test(f.name));
    const base = (n: string) => n.replace(/\.[^.]+$/, '');
    let pl = this.playlist(LOCAL_PLAYLIST_ID);
    if (!pl) {
      pl = { id: LOCAL_PLAYLIST_ID, title: '本地导入', subtitle: '仅本次浏览器会话有效', kind: 'local', tracks: [] };
      this.playlists.push(pl);
    }
    const added: string[] = [];
    for (const f of audio) {
      const id = 'local-' + base(f.name) + '-' + f.size;
      if (!this.tracks.has(id)) {
        this.tracks.set(id, {
          id,
          title: base(f.name),
          artist: '本地文件',
          kind: 'local',
          src: URL.createObjectURL(f),
          scene: 'jindian',
          ambientText: '南无阿弥陀佛',
        });
        pl.tracks.push(id);
      }
      const tf = timelines.find((t) => base(t.name) === base(f.name));
      if (tf) this.setInlineTimeline(id, parseTimeline(await tf.text(), id));
      added.push(id);
    }
    return added;
  }
}
